import React from 'react';

export type CvTrack = 'work' | 'education';

export interface CvEntry {
    id: string;
    title: string;
    /** Label for its chart bar. */
    short: string;
    /** Fallback label for narrow bars (phones), e.g. "Uni". */
    tiny?: string;
    place: string;
    start: number;
    /** null = ongoing */
    end: number | null;
    track: CvTrack;
    details: string;
}

// DESIGN.md → CV: gentian marks work, loden marks education.
export const TRACK_FILL: Record<CvTrack, string> = { work: 'fill-gentian', education: 'fill-loden' };
const ROWS: { track: CvTrack; label: string }[] = [
    { track: 'work', label: 'Work' },
    { track: 'education', label: 'Education' },
];

interface ChartProps {
    entries: CvEntry[];
    /** Fractional current year, e.g. 2026.8 */
    now: number;
    width: number;
    compact?: boolean;
}

/** Two-row time chart: one bar per entry, bar length = duration. Bars link to their entry. */
function Chart({ entries, now, width, compact = false }: ChartProps) {
    const first = Math.min(...entries.map(e => e.start));
    const labelW = compact ? 64 : 96;
    const right = 10;
    const X = (yr: number) => labelW + (yr - first) / (now - first) * (width - labelW - right);
    const rowH = compact ? 30 : 34;
    const gap = 14;
    const top = 6;
    const axisY = top + ROWS.length * (rowH + gap) + 4;
    const fontSize = compact ? 12 : 14;

    const tickStep = compact ? 4 : 2;
    const ticks: number[] = [];
    for (let t = first; t < now - (compact ? 2 : 0.8); t += tickStep) ticks.push(t);

    return (
        <svg viewBox={`0 0 ${width} ${axisY + 18}`} className="block w-full h-auto font-label" role="group" aria-label="Timeline of work and education">
            {ticks.map(t => (
                <line key={t} x1={X(t)} x2={X(t)} y1={top} y2={axisY - 4} className="stroke-rule" strokeDasharray="2 3" />
            ))}

            {ROWS.map((row, r) => {
                const y = top + r * (rowH + gap);
                return (
                    <g key={row.track}>
                        <text x={0} y={y + rowH / 2 + 5} className="fill-ink" style={{ fontSize: fontSize + 1, fontWeight: 600 }}>
                            {row.label}
                        </text>
                        <line x1={labelW} x2={width - right} y1={y + rowH / 2} y2={y + rowH / 2} className="stroke-rule" />
                        {entries
                            .filter(e => e.track === row.track)
                            .sort((a, b) => a.start - b.start)
                            .map((e, i, rowEntries) => {
                            const xa = X(e.start) + 1;
                            const w = X(e.end ?? now) - 1 - xa;
                            const textW = (t: string) => t.length * fontSize * 0.5;
                            // Free space to the left: back to the previous bar in this row, or the row label.
                            const prev = rowEntries[i - 1];
                            const spaceLeft = xa - (prev ? X(prev.end ?? now) : labelW) - 8;

                            // Label placement: full name inside → full name left of the bar → short form inside.
                            let label: { text: string; inside: boolean } | null = null;
                            if (w > textW(e.short) + 14) label = { text: e.short, inside: true };
                            else if (spaceLeft > textW(e.short) + 8) label = { text: e.short, inside: false };
                            else if (e.tiny && w > textW(e.tiny) + 12) label = { text: e.tiny, inside: true };

                            return (
                                <a
                                    key={e.id}
                                    href={`#cv-${e.id}`}
                                    aria-label={`${e.title}, ${e.start} to ${e.end ?? 'today'}`}
                                    className="group outline-none"
                                >
                                    <rect
                                        x={xa} y={y} width={w} height={rowH} rx={2}
                                        className={`${TRACK_FILL[e.track]} stroke-ink group-hover:opacity-85 group-focus-visible:stroke-[3px]`}
                                        strokeWidth={0}
                                    />
                                    {label && (
                                        <text
                                            x={label.inside ? xa + 7 : xa - 8}
                                            y={y + rowH / 2 + 5}
                                            textAnchor={label.inside ? 'start' : 'end'}
                                            className={`${label.inside ? 'fill-white' : 'fill-ink'} pointer-events-none`}
                                            style={{ fontSize, fontWeight: 600 }}
                                        >
                                            {label.text}
                                        </text>
                                    )}
                                </a>
                            );
                        })}
                    </g>
                );
            })}

            {ticks.map(t => (
                <text key={t} x={X(t)} y={axisY + 12} textAnchor="middle" className="fill-muted" style={{ fontSize: 13 }}>{t}</text>
            ))}
            <line x1={X(now)} x2={X(now)} y1={top - 4} y2={axisY - 2} className="stroke-ink" strokeWidth={1.5} />
            <text x={X(now)} y={axisY + 12} textAnchor="end" className="fill-ink" style={{ fontSize: 13, fontWeight: 600 }}>today</text>
        </svg>
    );
}

export const CvChart: React.FC<{ entries: CvEntry[]; now: number }> = ({ entries, now }) => (
    <figure className="m-0 rounded border border-rule bg-surface px-3 pt-4 pb-3 sm:px-5 sm:pt-5">
        <div className="hidden sm:block"><Chart entries={entries} now={now} width={1000} /></div>
        <div className="sm:hidden"><Chart entries={entries} now={now} width={340} compact /></div>
        <figcaption className="label mt-2">
            Bar length shows how long each stage lasted. Select a bar to jump to it.
        </figcaption>
    </figure>
);
